import React, { useEffect, useRef } from 'react';
import { BackIcon, CloseIcon } from '../protocols/wizard/WizardShell';
import './Peptides101.css';

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="13 6 19 12 13 18" />
  </svg>
);

export const GuideScreen: React.FC<{
  eyebrow?: string;
  question: string;
  lede?: string;
  children: React.ReactNode;
  onBack?: () => void;
  onClose?: () => void;
  next?: { label?: string; onNext: () => void; disabled?: boolean };
  /** Optional text link under the primary CTA (e.g. "How to find a source"). */
  secondary?: { label: string; onClick: () => void };
  progress?: { index: number; total: number };
  /** Optional brand/sign-in row (logged-out visitors). Rendered inside the
   *  sticky header so it shares the single top safe-area inset and locks too. */
  brandRow?: React.ReactNode;
}> = ({ eyebrow = 'PEPTIDES 101', question, lede, children, onBack, onClose, next, secondary, progress, brandRow }) => {
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => { headingRef.current?.focus(); }, [question]);

  return (
    <div className="lp-screen">
      {(onBack || onClose || progress || brandRow) && (
        <div className="lp-topbar">
          {brandRow && <div className="lp-brandrow">{brandRow}</div>}
          <div className="lp-navrow">
            {onBack ? (
              <button className="lp-icon-btn" onClick={onBack} aria-label="Back"><BackIcon /></button>
            ) : <span className="lp-icon-spacer" />}
            {progress && (
              <div className="lp-dots" role="progressbar" aria-valuemin={1} aria-valuenow={progress.index + 1} aria-valuemax={progress.total} aria-label={`Step ${progress.index + 1} of ${progress.total}`}>
                {Array.from({ length: progress.total }).map((_, i) => (
                  <span key={i} className={`lp-dot${i === progress.index ? ' lp-dot-active' : ''}${i < progress.index ? ' lp-dot-done' : ''}`} />
                ))}
              </div>
            )}
            {onClose ? (
              <button className="lp-icon-btn" onClick={onClose} aria-label="Close"><CloseIcon /></button>
            ) : <span className="lp-icon-spacer" />}
          </div>
        </div>
      )}
      <div className="lp-question-block">
        <div className="lp-eyebrow">{eyebrow}</div>
        <h1 className="lp-question" ref={headingRef} tabIndex={-1}>{question}</h1>
        {lede && <p className="lp-lede">{lede}</p>}
      </div>
      <div className="lp-input-area">{children}</div>
      {(next || secondary) && (
        <div className="lp-footer">
          {next && (
            <button className="lp-cta" disabled={next.disabled} onClick={next.onNext}>
              {next.label ?? 'Next'} <ArrowIcon />
            </button>
          )}
          {secondary && (
            <button type="button" className="lp-secondary" onClick={secondary.onClick}>
              {secondary.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
